import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Skeleton,
  Stack,
} from '@mui/material';
import { dashboardCardSx } from '../card-style';

// Skeletons desta tela seguem o mesmo layout dos cards reais (ver index.js), pra nao
// "pular" quando os dados chegam. Cada peca abaixo espelha um componente real:
// KpiCard, TicketsByAgent/PerformancePanel (CardHeader+linhas), ChannelDonut
// (CardHeader+circulo), ScorePanel usado por CsatTable/NpsTable (CardHeader com badge
// de score + tabela) e HourlyProductivityChart (CardHeader+grafico).

function KpiCardSkeleton() {
  return (
    <Card sx={dashboardCardSx}>
      <CardContent>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
          <Skeleton variant="circular" width={36} height={36} />
          <Skeleton width="55%" height={20} />
        </Stack>
        <Skeleton width="40%" height={44} />
        <Skeleton width="70%" height={18} sx={{ mt: 0.5 }} />
        <Skeleton variant="rounded" height={40} sx={{ mt: 1.5 }} />
      </CardContent>
    </Card>
  );
}

function CardHeaderSkeleton({ titleWidth = '45%' }) {
  return (
    <CardHeader
      avatar={<Skeleton variant="circular" width={24} height={24} />}
      title={<Skeleton width={titleWidth} height={24} />}
    />
  );
}

function ListRowsCardSkeleton({ rows = 5, titleWidth }) {
  return (
    <Card sx={dashboardCardSx}>
      <CardHeaderSkeleton titleWidth={titleWidth} />
      <Divider />
      <CardContent>
        {Array.from({ length: rows }).map((_, index) => (
          <Stack key={index} direction="row" spacing={2} alignItems="center" sx={{ mb: 1.75 }}>
            <Skeleton width="22%" height={20} />
            <Skeleton variant="rounded" width="100%" height={16} sx={{ flex: 1 }} />
          </Stack>
        ))}
      </CardContent>
    </Card>
  );
}

function DonutCardSkeleton() {
  return (
    <Card sx={dashboardCardSx}>
      <CardHeaderSkeleton titleWidth="55%" />
      <Divider />
      <CardContent>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={7} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Skeleton variant="circular" width={180} height={180} />
          </Grid>
          <Grid item xs={12} md={5}>
            <Stack spacing={1.5}>
              {Array.from({ length: 4 }).map((_, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="center">
                  <Skeleton variant="rounded" width={14} height={14} />
                  <Skeleton width={`${80 - index * 10}%`} height={18} />
                </Stack>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

function ScorePanelSkeleton({ rows = 5 }) {
  return (
    <Card sx={dashboardCardSx}>
      <CardHeader
        avatar={<Skeleton variant="circular" width={24} height={24} />}
        title={<Skeleton width="60%" height={24} />}
        action={<Skeleton variant="rounded" width={72} height={48} />}
      />
      <Divider />
      <CardContent>
        <Skeleton width="35%" height={20} sx={{ mb: 1.5 }} />
        {Array.from({ length: rows }).map((_, index) => (
          <Stack key={index} direction="row" spacing={2} alignItems="center" sx={{ mb: 1.25 }}>
            <Skeleton variant="rounded" width={16} height={16} />
            <Skeleton width="35%" height={18} />
            <Skeleton width="15%" height={18} sx={{ ml: 'auto' }} />
            <Skeleton width="15%" height={18} />
          </Stack>
        ))}
      </CardContent>
    </Card>
  );
}

function PerformanceCardSkeleton() {
  return (
    <Card sx={dashboardCardSx}>
      <CardHeaderSkeleton titleWidth="40%" />
      <Divider />
      <CardContent>
        {Array.from({ length: 3 }).map((_, index) => (
          <Stack key={index} direction="row" spacing={2} alignItems="center" sx={{ mb: 2.25 }}>
            <Stack sx={{ flex: 1 }}>
              <Skeleton width="65%" height={18} />
              <Skeleton width="40%" height={30} />
            </Stack>
            <Skeleton variant="rounded" width="30%" height={32} />
          </Stack>
        ))}
      </CardContent>
    </Card>
  );
}

function ChartCardSkeleton({ height = 280 }) {
  return (
    <Card sx={dashboardCardSx}>
      <CardHeaderSkeleton titleWidth="50%" />
      <Divider />
      <CardContent>
        <Skeleton variant="rounded" width="100%" height={height} />
      </CardContent>
    </Card>
  );
}

// Retorna sempre o mesmo "formato" (uma lista de <Grid item>, sem container proprio) --
// tanto no loading quanto nos children reais -- porque este componente e usado como
// filho direto do <Grid container> da pagina (ver index.js), nao como um container a parte.
export default function DashboardSkeleton({ loading = false, children }) {
  if (!loading) {
    return <>{children}</>;
  }

  return (
    <>
      <Grid item xs={12} sm={6} md={2.4}><KpiCardSkeleton /></Grid>
      <Grid item xs={12} sm={6} md={2.4}><KpiCardSkeleton /></Grid>
      <Grid item xs={12} sm={6} md={2.4}><KpiCardSkeleton /></Grid>
      <Grid item xs={12} sm={6} md={2.4}><KpiCardSkeleton /></Grid>
      <Grid item xs={12} sm={6} md={2.4}><KpiCardSkeleton /></Grid>

      <Grid item xs={12} md={7}><ListRowsCardSkeleton rows={5} titleWidth="50%" /></Grid>
      <Grid item xs={12} md={5}><DonutCardSkeleton /></Grid>

      <Grid item xs={12} md={6}><ScorePanelSkeleton rows={5} /></Grid>
      <Grid item xs={12} md={6}><ScorePanelSkeleton rows={3} /></Grid>

      <Grid item xs={12} md={4}><PerformanceCardSkeleton /></Grid>
      <Grid item xs={12} md={8}><ChartCardSkeleton height={280} /></Grid>
    </>
  );
}
